import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingFormData } from '../types';
import { usersAPI, appointmentsAPI } from '../services/api';
import Step1BasicInfo from '../components/booking/Step1BasicInfo';
import Step2Complete from '../components/booking/Step2NationalId';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { publicAssetPath } from '../utils/publicAssetPath';

const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>({} as BookingFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingBookingStatus, setCheckingBookingStatus] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [bookingDisabledMessage, setBookingDisabledMessage] = useState('در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید.');
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(null);
  const [autoSavedAppointmentId, setAutoSavedAppointmentId] = useState<number | null>(null);
  /** نوبت در صف بررسی — UI اختصاصی به‌جای فرم */
  const [queueConflictActive, setQueueConflictActive] = useState(false);

  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 py-2.5 px-4 text-sm font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300';

  useEffect(() => {
    const fetchBookingStatus = async () => {
      try {
        const response = await appointmentsAPI.getBookingStatus();
        const data = response.data;
        setBookingEnabled(data.booking_enabled !== false);
        setBookingDisabledMessage(
          data.booking_disabled_message?.trim() || 'در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید.'
        );
      } catch (err) {
        console.error('Error fetching booking status:', err);
      } finally {
        setCheckingBookingStatus(false);
      }
    };
    void fetchBookingStatus();
  }, []);

  const handleStepNext = async (stepData: any) => {
    if (!bookingEnabled) {
      setError(bookingDisabledMessage);
      return;
    }

    const newFormData = { ...formData, ...stepData };
    setFormData(newFormData);
    setIsSubmitting(true);
    setQueueConflictActive(false);
    setError(null);

    try {
      if (currentStep === 1) {
        // اگر قبلاً auto-save شده، از همان استفاده کن
        if (autoSavedAppointmentId && stepData.user_id) {
          newFormData.user_id = stepData.user_id;
          newFormData.appointment_id = autoSavedAppointmentId;
          setFormData(newFormData);
        } else {
          // دریافت یا ایجاد کاربر
          const userResponse = await usersAPI.getOrCreate({
            full_name: stepData.full_name,
            phone: stepData.phone
          });

          newFormData.user_id = userResponse.data.id;
          setFormData(newFormData);

          // ایجاد appointment ناتمام اگر وجود ندارد
          if (!autoSavedAppointmentId) {
            try {
              const appointmentResponse = await appointmentsAPI.create({
                user_id: userResponse.data.id,
                service_id: undefined
              });
              newFormData.appointment_id = appointmentResponse.data.id;
              setAutoSavedAppointmentId(appointmentResponse.data.id);
            } catch (err) {
              console.error('Error creating incomplete appointment:', err);
              throw err;
            }
          }
        }
      } else if (currentStep === 2) {
        // تکمیل نوبت - همه کارها در یک مرحله
        if (!newFormData.user_id) {
          throw new Error('User ID not found');
        }

        // به‌روزرسانی کد ملی
        await usersAPI.update(newFormData.user_id, {
          national_id: stepData.national_id
        });

        // اگر appointment قبلاً ایجاد شده (auto-save)، آن را به‌روزرسانی کن
        let createdAppointmentId: number;
        if (newFormData.appointment_id || autoSavedAppointmentId) {
          createdAppointmentId = newFormData.appointment_id || autoSavedAppointmentId!;
          // به‌روزرسانی appointment با service_id
          await appointmentsAPI.update(createdAppointmentId, {
            service_ids: stepData.service_ids,
          });
        } else {
          // ایجاد نوبت جدید
          const appointmentResponse = await appointmentsAPI.create({
            user_id: newFormData.user_id,
            service_ids: stepData.service_ids,
          });
          createdAppointmentId = appointmentResponse.data.id;
        }

        // دریافت appointment نهایی
        const appointmentResponse = await appointmentsAPI.getById(createdAppointmentId);
        const createdAppointment = appointmentResponse.data;
        const createdTrackingCode = createdAppointment.tracking_code || createdAppointment.prescription_tracking_code || stepData.prescription_tracking_code || null;
        setAppointmentId(createdAppointmentId);
        setTrackingCode(createdTrackingCode);
        newFormData.appointment_id = createdAppointmentId;
        newFormData.tracking_code = createdTrackingCode || undefined;
        setFormData({ ...newFormData });

        // تکمیل نوبت
        await appointmentsAPI.updateStep(createdAppointmentId, 2, {
          prescription_tracking_code: stepData.prescription_tracking_code
        });

        const prescriptionFiles =
          stepData.prescription_files?.length
            ? stepData.prescription_files
            : stepData.prescription_file
              ? [stepData.prescription_file]
              : [];
        for (const file of prescriptionFiles) {
          await appointmentsAPI.uploadPrescription(createdAppointmentId, file);
        }
      }

      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        // تکمیل فرآیند
        navigate('/booking/success', {
          state: {
            appointmentId: appointmentId ?? newFormData.appointment_id,
            trackingCode: trackingCode ?? newFormData.tracking_code,
            formData: newFormData
          }
        });
      }
    } catch (err: any) {
      console.error('Error in booking process:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);

      if (err.response?.status === 409) {
        setQueueConflictActive(true);
        setError(null);
        return;
      }

      let errorMessage = 'خطا در ثبت اطلاعات';

      if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        errorMessage = typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: unknown) => String(x)).join(' ') : errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStepBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div key="step1" className="animate-fadeIn">
            <Step1BasicInfo
              onNext={handleStepNext}
              initialData={formData}
              onAutoSave={(appointmentId) => {
                setAutoSavedAppointmentId(appointmentId);
              }}
            />
          </div>
        );
      case 2:
        return (
          <div key="step2" className="animate-fadeIn">
            <Step2Complete
              onNext={handleStepNext}
              onBack={handleStepBack}
              initialData={formData}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-[100dvh] bg-white flex flex-col overflow-y-auto touch-pan-y">
      <div className="w-full max-w-3xl mx-auto px-0 sm:px-6 lg:px-8 py-4 sm:py-12 flex-grow flex flex-col">
        <div className="mb-6 sm:mb-10 text-center px-4 flex flex-col items-center">
          <img src={publicAssetPath('/logo.png')} alt="لوگو پایتخت" className="w-20 h-20 object-contain mb-3" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">رزرو نوبت</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            {queueConflictActive
              ? 'وضعیت نوبت قبلی شما را در پایین می‌بینید.'
              : 'لطفاً اطلاعات خود را تکمیل کنید.'}
          </p>
        </div>

        {checkingBookingStatus ? (
          <div className="bg-white sm:bg-gray-50 sm:border sm:border-gray-200 rounded-none sm:rounded-2xl shadow-none sm:shadow-sm px-4 sm:px-10 py-10 sm:py-16">
            <LoadingSpinner text="در حال بررسی وضعیت رزرو..." />
          </div>
        ) : !bookingEnabled ? (
          <div className="bg-white sm:bg-gray-50 sm:border sm:border-gray-200 rounded-none sm:rounded-2xl shadow-none sm:shadow-sm px-4 sm:px-10 py-8 sm:py-12">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-5 text-gray-800">
              <h2 className="text-lg sm:text-xl font-bold text-amber-800">رزرو غیرفعال است</h2>
              <p className="mt-3 text-sm sm:text-base leading-7 text-amber-900">{bookingDisabledMessage}</p>
            </div>
          </div>
        ) : (
        <div className="bg-white sm:bg-gray-50 sm:border sm:border-gray-200 rounded-none sm:rounded-2xl shadow-none sm:shadow-sm px-4 sm:px-10 py-4 sm:py-8 flex-grow flex flex-col">
          {queueConflictActive ? (
            <div className="flex flex-col items-center text-center py-6 sm:py-10 animate-fadeIn">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-200/80 to-indigo-200/60 blur-xl scale-150" aria-hidden />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 shadow-lg shadow-indigo-500/25">
                  <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                نوبت شما در صف بررسی مرکز است
              </h2>
              <p className="mt-4 max-w-md text-sm sm:text-base leading-7 text-gray-600">
                با همین شماره موبایل قبلاً نوبتی ثبت کرده‌اید که هنوز توسط مرکز بررسی نشده است. تا تعیین تکلیف آن نوبت، امکان ثبت نوبت جدید نیست.
              </p>
              <p className="mt-3 max-w-md text-xs sm:text-sm leading-6 text-gray-500">
                اگر سوالی دارید با مرکز تماس بگیرید؛ پس از تکمیل یا لغو نوبت قبلی می‌توانید دوباره از این صفحه رزرو کنید.
              </p>
              <button
                type="button"
                onClick={() => setQueueConflictActive(false)}
                className="mt-8 inline-flex items-center justify-center rounded-xl bg-gray-900 px-8 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
              >
                متوجه شدم
              </button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="flex items-center justify-between text-sm font-medium text-gray-600">
                  <span className={currentStep === 1 ? 'text-gray-900 font-semibold' : ''}>مرحله ۱: اطلاعات اولیه</span>
                  <span className={currentStep === 2 ? 'text-gray-900 font-semibold' : ''}>مرحله ۲: تکمیل نوبت</span>
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-gray-700 transition-all duration-300"
                    style={{ width: currentStep === 1 ? '50%' : '100%' }}
                  />
                </div>
              </div>

              {error && (
                <div className="mb-6 rounded-xl border border-red-200 bg-white px-4 py-3 text-sm text-gray-800">
                  <div className="flex flex-col gap-3">
                    <div className="font-semibold text-red-600">خطا در ثبت اطلاعات</div>
                    <p className="leading-6 text-gray-600">{error}</p>
                    <div className="text-left">
                      <button
                        type="button"
                        onClick={() => setError(null)}
                        className={secondaryButtonClass}
                      >
                        بستن
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isSubmitting ? (
                <LoadingSpinner text="در حال ثبت اطلاعات..." />
              ) : (
                renderCurrentStep()
              )}
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
